import { Controller } from 'stimulus';

const CLASS_HIDDEN = 'd-none';
const CLASS_ERROR = 'amba-file-upload-feedback-file-container--error';
const CLASS_DRAGOVER = 'amba-file-upload--dragover';

const EVENTS_DRAG_START = ['dragover', 'dragenter'];
const EVENTS_DRAG_END = ['dragleave', 'dragleave', 'drop'];

export default class extends Controller {
  static targets = [
    'uploadContainer',
    'feedbackContainer',
    'feedbackFile',
    'submitButton',
    'errorFlash',
  ];
  static values = { redirectUrl: String };

  connect() {
    this.fileCache = [];
    this.isSubmitting = false;
    this.setupDragStateListeners();
    this.element.addEventListener('submit', this.sendData);

    this.setupRemoveFileListener();
    this.submitButtonTarget.classList.add(CLASS_HIDDEN);
    this.feedbackContainerTarget.classList.add(CLASS_HIDDEN);
    this.cacheSubmitButtonContent();
  }

  addFilesToFileCache = ({ target }) => {
    Object.entries(target.files).forEach((file) => {
      this.fileCache.push(file[1]);
      this.addFeedbackElFor(file[1]);
    });

    this.updateControlVisibility();
  };

  sendData = async (event) => {
    event.preventDefault();

    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.disableSubmitButton();

    try {
      const response = await fetch(this.element.action, {
        method: 'POST',
        headers: this.headers,
        body: this.formData,
      });

      if (response.ok) {
        window.location.href = this.redirectUrlValue;
        return;
      }

      const textResponse = await response.text();
      const resultsArray = JSON.parse(textResponse);

      this.updateFilesAfterValidation(resultsArray);
    } catch (error) {
      this.enableForm();
      window.sendError(error);
    }
  };

  updateFilesAfterValidation(results) {
    results.forEach(([name, error]) => {
      const feedbackEl = this.feedbackFileTargets.find(
        (el) => el.dataset.fileName === name
      );

      if (error) {
        feedbackEl.classList.add(CLASS_ERROR);
        feedbackEl.querySelector('.invalid-feedback')?.remove();
        feedbackEl.insertAdjacentHTML(
          'beforeend',
          `<div class="invalid-feedback">${error}</div>`
        );
      }
    });

    this.errorFlashTarget.classList.remove(CLASS_HIDDEN);
    this.enableForm();
  }

  get formData() {
    const formData = new FormData();

    this.fileCache.forEach((file) => {
      if (file) {
        formData.append('organisation_file[file][]', file);
      }
    });

    return formData;
  }

  enableForm() {
    this.isSubmitting = false;
    this.enableSubmitButton();
  }

  addFeedbackElFor(file) {
    let fileSize = file.size.toString();

    if (fileSize.length < 7) {
      fileSize = `${Math.round(+fileSize / 1024).toFixed(1)}kb`;
    } else {
      fileSize = `${(Math.round(+fileSize / 1024) / 1000).toFixed(1)}MB`;
    }

    const fileId = this.fileCache.indexOf(file);

    const el = `
      <div data-file-name="${file.name}" data-file-upload-target="feedbackFile" class="amba-file-upload-feedback-file-container">
        <div class="amba-file-upload-feedback-file spacing-x-2">
          <i class="fas fa-file-alt fs-1 text-primary"></i>
          <div class="d-flex flex-column">
            <span class="me-auto">${file.name}</span>
            <span class="fs-tiny">${fileSize}</span>
          </div>
          <button type="button" class="ms-auto" data-file-upload-remove-id="${fileId}">
            <i class="fas fa-times text-primary pe-none"></i>
            <span class="visually-hidden">Remove file</span>
          </button>
        </div>
      </div>
    `;

    this.feedbackContainerTarget.insertAdjacentHTML('afterbegin', el);
  }

  setupRemoveFileListener() {
    this.element.addEventListener('click', (event) => {
      if (event.target.hasAttribute('data-file-upload-remove-id')) {
        const id = event.target.dataset.fileUploadRemoveId;
        this.fileCache[id] = undefined;

        const feedbackEl = event.target.closest(
          '[data-file-upload-target="feedbackFile"]'
        );
        feedbackEl.remove();
      }

      this.updateControlVisibility();

      if (!this.feedbackContainerTarget.querySelector('.invalid-feedback')) {
        this.errorFlashTarget.classList.add(CLASS_HIDDEN);
      }
    });
  }

  updateControlVisibility() {
    const anyFilePresent = this.fileCache.some((file) => file !== undefined);

    this.feedbackContainerTarget.classList.toggle(
      CLASS_HIDDEN,
      !anyFilePresent
    );
    this.submitButtonTarget.classList.toggle(CLASS_HIDDEN, !anyFilePresent);
  }

  setupDragStateListeners() {
    EVENTS_DRAG_START.forEach((eventName) => {
      this.uploadContainerTarget.addEventListener(eventName, () => {
        this.uploadContainerTarget.classList.add(CLASS_DRAGOVER);
      });
    });

    EVENTS_DRAG_END.forEach((eventName) => {
      this.uploadContainerTarget.addEventListener(eventName, () => {
        this.uploadContainerTarget.classList.remove(CLASS_DRAGOVER);
      });
    });
  }

  cacheSubmitButtonContent() {
    if (!this.submitButtonTarget.dataset.originalContent) {
      this.submitButtonTarget.dataset.originalContent =
        this.submitButtonTarget.innerHTML;
    }
  }

  disableSubmitButton() {
    this.submitButtonTarget.disabled = true;
    this.submitButtonTarget.innerHTML =
      '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i><span class="visually-hidden">Uploading</span>';
  }

  enableSubmitButton() {
    this.submitButtonTarget.disabled = false;
    this.submitButtonTarget.innerHTML =
      this.submitButtonTarget.dataset.originalContent;
  }

  get headers() {
    return {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
        ?.content,
      Accept: 'application/json; charset=UTF-8',
    };
  }
}
